import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ResumeAnalysisService from '../../services/resumeAnalysisService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ScanBadge from '../user-dashboard/components/ScanBadge';
import Button from '../../components/ui/Button';
import { getProgressMap, upsertProgress, getProgressHistory } from '../../services/learningProgressService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = (userId) => `aa_learning_progress_${userId || 'anon'}`;

const SkillGapLearningPath = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [analyses, setAnalyses] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [filterPriority, setFilterPriority] = useState('All');
  const [sortBy, setSortBy] = useState('priority');

  const [historySeries, setHistorySeries] = useState([]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        if (!user?.id) return;
        const history = await getProgressHistory(user.id, 60);
        // Build cumulative series per day
        const countsByDay = {};
        (history || []).forEach(h => {
          const d = h.completed_at ? new Date(h.completed_at) : null;
          if (!d) return;
          const key = d.toISOString().slice(0, 10); // YYYY-MM-DD
          countsByDay[key] = (countsByDay[key] || 0) + 1;
        });
        const days = Object.keys(countsByDay).sort();
        let cum = 0;
        const series = days.map(day => {
          cum += countsByDay[day];
          return { name: day, completed: cum };
        });
        setHistorySeries(series);
      } catch { }
    };
    loadHistory();
  }, [user?.id]);

  useEffect(() => {
    const load = async () => {
      try {
        if (!user?.id) return;
        setLoading(true);
        const list = await ResumeAnalysisService.getUserAnalyses(user.id, 20);
        setAnalyses(list || []);
      } catch (e) {
        console.error('Skill gaps load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id]);

  useEffect(() => {
    const load = async () => {
      try {
        if (!user?.id) return;
        // prefer server truth; fallback to cache to avoid flicker
        const cached = localStorage.getItem(STORAGE_KEY(user.id));
        if (cached) setProgressMap(JSON.parse(cached));
        const map = await getProgressMap(user.id);
        setProgressMap(map);
        localStorage.setItem(STORAGE_KEY(user.id), JSON.stringify(map));
      } catch {
        // ignore
      }
    };
    load();
  }, [user?.id]);

  const saveProgress = async (next, changedItem = null) => {
    setProgressMap(next);
    try {
      if (user?.id && changedItem) {
        await upsertProgress(user.id, {
          id: changedItem.id,
          completed: !!next[changedItem.id],
          analysis_id: changedItem.analysis_id,
          skill: changedItem.skill,
          type: changedItem.type,
          url: changedItem.url
        });
      }
      localStorage.setItem(STORAGE_KEY(user?.id), JSON.stringify(next));
    } catch { }
  };

  const safeTitle = (posting, fallback) =>
    typeof posting?.title === 'string' ? posting.title : (fallback || 'Live Analysis');

  const groupedByAnalysis = useMemo(() => {
    // Calculate scan numbers first
    const jobCounts = {};
    const analysesWithScanNumbers = (analyses || []).map(a => {
      const posting = a?.analysis_results?.jobPosting || {};
      const title = safeTitle(posting, a?.file_name);
      const jobKey = a.target_role || title || 'unknown';
      jobCounts[jobKey] = (jobCounts[jobKey] || 0) + 1;
      return { ...a, scanNumber: jobCounts[jobKey] };
    });

    const groups = analysesWithScanNumbers
      .map((a) => {
        const live = a?.analysis_results?.analysis;
        if (!live) return null;

        const posting = a?.analysis_results?.jobPosting || {};
        const title = safeTitle(posting, a?.file_name);
        const created_at = a?.created_at || null;

        const roadmap = Array.isArray(live?.skill_roadmap) ? live.skill_roadmap : [];
        const gaps = Array.isArray(live?.skills_missing) ? live.skills_missing : [];

        const gapPriorityBySkill = {};
        gaps.forEach((g) => {
          const name = (g?.name || '').trim();
          if (!name) return;
          const sev = (g?.severity || 'Recommended').toLowerCase();
          const priority = sev === 'critical' ? 'Critical' : sev === 'bonus' ? 'Bonus' : 'Recommended';
          gapPriorityBySkill[name.toLowerCase()] = priority;
        });

        const items = [];
        roadmap.forEach((r) => {
          const skill = (r?.skill || '').trim() || 'Skill';
          const key = skill.toLowerCase();
          const priority = gapPriorityBySkill[key] || 'Recommended';

          if (r?.premium_course?.url) {
            items.push({
              id: `${a.id}-course-${skill}-${r.premium_course.url}`,
              type: 'Course',
              skill,
              title: r.premium_course.title || 'Recommended course',
              provider: r.premium_course.provider || '',
              url: r.premium_course.url,
              priority
            });
          }
          if (r?.youtube_top?.url) {
            items.push({
              id: `${a.id}-video-${skill}-${r.youtube_top.url}`,
              type: 'Video',
              skill,
              title: r.youtube_top.title || 'Watch video',
              provider: r.youtube_top.channel || '',
              url: r.youtube_top.url,
              priority
            });
          }
          if (!r?.premium_course?.url && !r?.youtube_top?.url) {
            items.push({
              id: `${a.id}-action-${skill}-${items.length}`,
              type: 'Action',
              skill,
              title: r?.why_it_matters || 'Focus on this skill',
              provider: '',
              url: '',
              priority
            });
          }
        });

        const filtered =
          filterPriority === 'All' ? items : items.filter((it) => it.priority === filterPriority);

        const priorityOrder = { Critical: 0, Recommended: 1, Bonus: 2 };
        const sorted =
          sortBy === 'priority'
            ? filtered.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
            : filtered;

        return {
          id: a.id,
          title,
          created_at,
          items: sorted,
          scanNumber: a.scanNumber
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

    return groups;
  }, [analyses, filterPriority, sortBy]);

  const analysisSummaries = useMemo(() => {
    const jobCounts = {};
    return (analyses || [])
      .map((a) => {
        const liveA = a?.analysis_results?.analysis;
        const posting = a?.analysis_results?.jobPosting || {};
        const title = typeof posting?.title === 'string' ? posting.title : (a?.target_role || 'Analysis');
        const jm = typeof liveA?.job_match_score === 'number' ? liveA.job_match_score : (a?.job_match_score || 0);

        // Calculate scan number
        const jobKey = a.target_role || title || 'unknown';
        jobCounts[jobKey] = (jobCounts[jobKey] || 0) + 1;

        return {
          id: a.id,
          title,
          jm,
          date: a?.created_at || null,
          scanNumber: jobCounts[jobKey]
        };
      })
      .filter(s => s.title);
  }, [analyses]);

  const { totalItems, completedItems } = useMemo(() => {
    let total = 0;
    let done = 0;
    groupedByAnalysis.forEach((g) => {
      g.items.forEach((it) => {
        total += 1;
        if (progressMap[it.id]) done += 1;
      });
    });
    return { totalItems: total, completedItems: done };
  }, [groupedByAnalysis, progressMap]);

  const percent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const toggleItem = (item) => {
    const next = { ...progressMap, [item.id]: !progressMap[item.id] };
    saveProgress(next, item);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading learning path...</span>
        </div>
      </div>
    );
  }

  const latest = (analyses || []).find(a => a?.analysis_results?.analysis) || null;
  const live = latest?.analysis_results?.analysis || {};
  const posting = latest?.analysis_results?.jobPosting || {};

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Analyses Overview (all live runs) */}
          <div className="bg-card border border-border rounded-lg p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Analyses Overview</h2>
              <span className="text-xs text-muted-foreground">{analysisSummaries.length} total</span>
            </div>

            {analysisSummaries.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No analyses yet. Run a live analysis to see results here.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {analysisSummaries.map(s => (
                  <div key={s.id} className="border border-border rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-foreground">{s.title}</div>
                        <ScanBadge scanNumber={s.scanNumber} />
                      </div>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        {s.date ? new Date(s.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                        {s.jm}% Match
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main - grouped by analysis */}
            <div className="lg:col-span-3 space-y-6">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Filter" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-card-foreground">Filter:</span>
                    <select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e?.target?.value)}
                      className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
                    >
                      <option value="All">All Priorities</option>
                      <option value="Critical">Critical</option>
                      <option value="Recommended">Recommended</option>
                      <option value="Bonus">Bonus</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-card-foreground">Sort:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e?.target?.value)}
                      className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
                    >
                      <option value="priority">Priority</option>
                      <option value="time" disabled>Time Required</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setExpandedGroup('ALL');
                    }}
                    iconName="Expand"
                    iconPosition="left"
                  >
                    Expand All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExpandedGroup(null)}
                    iconName="Minimize"
                    iconPosition="left"
                  >
                    Collapse All
                  </Button>
                </div>
              </div>

              {/* Groups */}
              {groupedByAnalysis.length === 0 && (
                <div className="text-center py-12 bg-card border border-border rounded-lg">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-card-foreground mb-2">No learning items yet</h3>
                  <p className="text-muted-foreground">Run a live analysis to generate a personalized learning path.</p>
                </div>
              )}

              {groupedByAnalysis.map((group) => {
                const open = expandedGroup === 'ALL' || expandedGroup === group.id;
                return (
                  <div key={group.id} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() =>
                        setExpandedGroup((curr) =>
                          curr === group.id ? null : group.id
                        )
                      }
                      className="w-full flex items-center justify-between px-4 py-3 bg-card hover:bg-muted transition-colors"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-foreground">{group.title}</div>
                          <ScanBadge scanNumber={group.scanNumber} />
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {group.created_at
                            ? new Date(group.created_at).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                            : ''}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {group.items.length} actions
                      </div>
                    </button>

                    {open && (
                      <div className="p-4 space-y-3 bg-background">
                        {group.items.map((item) => (
                          <label
                            key={item.id}
                            className="flex items-start justify-between gap-3 bg-muted/30 rounded-md px-3 py-2"
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 accent-primary"
                                checked={!!progressMap[item.id]}
                                onChange={() => toggleItem({ ...item, analysis_id: group.id })}
                              />
                              <div>
                                <div className="text-xs text-muted-foreground mb-0.5">
                                  {item.skill} · {item.priority}
                                </div>
                                <div className="text-sm font-medium text-foreground">
                                  {item.type}: {item.title}
                                </div>
                                {item.provider && (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {item.provider}
                                  </div>
                                )}
                              </div>
                            </div>
                            {item.url ? (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs px-2 py-1 rounded-md border border-border hover:bg-muted transition-colors"
                              >
                                Open
                              </a>
                            ) : (
                              <span className="text-xs text-muted-foreground px-2 py-1">
                                No link
                              </span>
                            )}
                          </label>
                        ))}
                        {group.items.length === 0 && (
                          <div className="text-sm text-muted-foreground">
                            No actions for this analysis with current filters.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Sidebar - Progress Summary only */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-card-foreground">Progress Summary</h3>
                  <Icon name="Activity" size={18} className="text-primary" />
                </div>
                <div className="text-3xl font-semibold text-foreground">{percent}%</div>
                <div className="text-xs text-muted-foreground mb-4">
                  {completedItems} of {totalItems} completed
                </div>

                <div className="w-full bg-muted rounded-full h-2 mb-2">
                  <div
                    className="h-2 rounded-full bg-primary transition-all"
                    style={{ width: `${percent}%` }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="bg-muted/40 rounded-md p-3 text-center">
                    <div className="text-lg font-semibold text-foreground">{completedItems}</div>
                    <div className="text-[11px] text-muted-foreground">Completed</div>
                  </div>
                  <div className="bg-muted/40 rounded-md p-3 text-center">
                    <div className="text-lg font-semibold text-foreground">{totalItems - completedItems}</div>
                    <div className="text-[11px] text-muted-foreground">Remaining</div>
                  </div>
                </div>

                <div className="h-32 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={historySeries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="completed" stroke="var(--color-primary)" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-4">

                  <div className="mt-2">

                  </div>
                </div>


                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { saveProgress({}); }}
                    iconName="RotateCcw"
                    iconPosition="left"
                    fullWidth
                  >
                    Reset Progress
                  </Button>
                  <div className="mt-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => navigate('/upload-analysis')}
                      iconName="Upload"
                      iconPosition="left"
                      fullWidth
                    >
                      New Analysis
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {/* End Sidebar */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SkillGapLearningPath;